/**
 * API Service Layer
 *
 * Provides a typed interface for all backend API endpoints.
 * Handles authentication, error responses, and request/response transformation.
 *
 * Usage:
 *   import { api } from '@/services/api';
 *   const status = await api.auth.getStatus();
 */

import { parseCarrierInitializationMappingDraftsResponse } from './carrierInitializationMappingResponse';

// ============================================
// Configuration
// ============================================

const API_BASE = '/api';

// ============================================
// Types
// ============================================

// Error response from the API
export interface ApiError {
  error: string;
  message: string;
  status: number;
  clarificationQuestions?: string[];
  preflight?: TrafficInvestigationPreflight;
}

// Custom error class for API errors
export class ApiRequestError extends Error {
  public readonly status: number;
  public readonly errorCode: string;
  public readonly clarificationQuestions: string[];
  public readonly preflight?: TrafficInvestigationPreflight;

  constructor(error: ApiError) {
    super(error.message);
    this.name = 'ApiRequestError';
    this.status = error.status;
    this.errorCode = error.error;
    this.clarificationQuestions = error.clarificationQuestions ?? [];
    this.preflight = error.preflight;
  }

  // Check if this is an authentication error
  isAuthError(): boolean {
    return this.status === 401;
  }

  // Check if this is a forbidden error
  isForbidden(): boolean {
    return this.status === 403;
  }

  // Check if this is a not found error
  isNotFound(): boolean {
    return this.status === 404;
  }
}

// ============================================
// Auth Types
// ============================================

export interface User {
  id: string | number;
  username: string;
  displayName?: string | null;
  isAdmin?: boolean;
  isSupportAdmin?: boolean;
  claudeConnected?: boolean;
}

export interface AuthStatus {
  authenticated: boolean;
  user?: User;
  needsSetup?: boolean;
}

export interface LoginResponse {
  success: boolean;
  user: User;
}

export interface LogoutResponse {
  success: boolean;
  message: string;
}

export interface SetupResponse {
  success: boolean;
  user: User;
}

// ============================================
// File Types
// ============================================

export interface FileEntry {
  name: string;
  path: string;
  type: 'file' | 'directory';
  size: number;
  mtime: string;
  extension?: string;
}

export interface TreeNode extends FileEntry {
  children?: TreeNode[];
}

export interface FileTreeResponse {
  path: string;
  tree: TreeNode[];
}

export interface FileListResponse {
  path: string;
  entries: FileEntry[];
}

export interface FileExistsResponse {
  exists: boolean;
  type?: 'file' | 'directory';
}

export interface FileReadResponse {
  path: string;
  content: string;
  mimeType: string;
  size: number;
}

export type ParquetJsonValue =
  | null
  | boolean
  | number
  | string
  | ParquetJsonValue[]
  | { [key: string]: ParquetJsonValue };

export interface ParquetColumn {
  name: string;
  dtype: string;
}

export interface ParquetPageResponse {
  path: string;
  page: number;
  pageSize: number;
  columns: ParquetColumn[];
  rows: Array<Record<string, ParquetJsonValue>>;
  totalRows: number;
  filteredRows: number;
  offset: number;
  limit: number;
}

export interface UploadResult {
  uploaded: FileEntry[];
  failed: { filename: string; error: string }[];
  summary: {
    total: number;
    successful: number;
    failed: number;
  };
}

// ============================================
// Workspace Asset Types
// ============================================

export type WorkspaceAssetScope = 'user' | 'shared';
export type WorkspaceAssetType = 'upload' | 'dashboard' | 'data';

export interface WorkspaceAsset {
  id: string;
  scope: WorkspaceAssetScope;
  type: WorkspaceAssetType;
  ownerUserId: number | null;
  title: string;
  name: string;
  mimeType: string | null;
  extension: string | null;
  size: number;
  backingPath: string;
  sourceFolder: string;
  createdAt: string;
  updatedAt: string;
  indexedAt: string;
}

export interface WorkspaceAssetsResponse {
  assets: WorkspaceAsset[];
}

export interface WorkspaceAssetResponse {
  asset: WorkspaceAsset;
}

export interface WorkspaceAssetUploadResult {
  uploaded: WorkspaceAsset[];
  failed: { filename: string; error: string }[];
  summary: {
    total: number;
    successful: number;
    failed: number;
  };
}

/**
 * Chunk size the browser sends, and the size above which an upload must use
 * the chunked path. Both stay under Cloudflare's approximate 100 MiB
 * request-body cap; the server enforces the same ceiling independently.
 */
export const UPLOAD_CHUNK_SIZE_BYTES = 64 * 1024 * 1024;
export const CHUNKED_UPLOAD_THRESHOLD_BYTES = 75 * 1024 * 1024;

export interface UploadSessionState {
  id: string;
  originalFilename: string;
  uploadFilename: string;
  destination: string;
  totalSizeBytes: number;
  chunkSizeBytes: number;
  chunkCount: number;
  status: 'pending' | 'assembling' | 'completed' | 'failed' | 'expired';
  finalPath: string | null;
  errorMessage: string | null;
  expiresAt: string;
  uploadedChunkIndexes: number[];
}

export interface MoveResult {
  source: string;
  destination: string;
  entry: FileEntry;
}

export interface MkdirResult {
  path: string;
  entry: FileEntry;
}

export interface DeleteResult {
  path: string;
  type: 'file' | 'directory';
  deleted: boolean;
}

export interface WriteResult {
  path: string;
  entry: FileEntry;
}

// ============================================
// Chat Types
// ============================================

export interface ChatSession {
  id: string;
  name: string;
  provider: 'claude' | 'codex';
  model: string | null;
  reasoningEffort: string | null;
  claudeSessionId: string | null;
  messageCount: number;
  createdAt: string;
  updatedAt: string;
  /** Null after the owner was deleted. */
  ownerUserId: number | null;
  /** "Deleted user" after the owner was deleted. */
  ownerUsername: string;
  ownerDeleted: boolean;
  forkedFromSessionId: string | null;
  /** The caller owns this chat and can write to it. */
  canPost: boolean;
  /** The caller is the owner or an admin. */
  canDelete: boolean;
}

export interface ApprovalInteractionOption {
  id: string;
  label: string;
}

export interface ApprovalInteractionRequest {
  kind: 'approval';
  version: 1;
  question: string;
  options: ApprovalInteractionOption[];
}

export interface ApprovalInteractionResponse {
  optionId: string;
  optionLabel: string;
}

export interface ServiceDefinitionFormValue {
  id: string;
  code: string;
  name: string;
  description: string | null;
  aliases: string[];
  // Optional tenant ACD range in seconds, half-open [min, max).
  minAcdSeconds?: number | null;
  maxAcdSeconds?: number | null;
}

export interface ServiceDefinitionFormSubmissionValue {
  id?: string;
  code: string;
  name: string;
  description?: string | null;
  aliases?: string[];
  minAcdSeconds?: number | null;
  maxAcdSeconds?: number | null;
}

interface ChatInteractionBase {
  id: string;
  sessionId: string;
  assistantMessageId: number;
  version: 1;
  status: 'pending' | 'submitted';
  revision: number;
  submittedBy: { id: number; username: string } | null;
  createdAt: string;
  submittedAt: string | null;
  resumeStreamId: string | null;
  resumeCompleted: boolean;
}

export interface ApprovalChatInteraction extends ChatInteractionBase {
  kind: 'approval';
  request: ApprovalInteractionRequest;
  response: ApprovalInteractionResponse | null;
}

export interface ServiceDefinitionFormInteraction extends ChatInteractionBase {
  kind: 'service_definition_form';
  request: {
    kind: 'service_definition_form';
    version: 1;
    title: string;
    instructions: string;
    definitions: ServiceDefinitionFormValue[];
  };
  response: { definitions: ServiceDefinitionFormValue[] } | null;
}

export type ChatInteraction =
  | ApprovalChatInteraction
  | ServiceDefinitionFormInteraction;

export type ChatInteractionSubmission =
  | { optionId: string }
  | { definitions: ServiceDefinitionFormSubmissionValue[] };

export interface ChatMessage {
  id: number;
  role: 'user' | 'assistant' | 'system';
  content: string;
  images: string[] | null;
  timestamp: string;
  interaction: ChatInteraction | null;
  isInteractionResponse: boolean;
  /** Server-observed data backing for an assistant answer; null when not recorded. */
  grounding?: 'connector' | 'workspace' | 'unsupported' | 'not_applicable' | null;
  /** The writer of a user message; null when none is recorded. A copied chat keeps the original writer. */
  authorUserId: number | null;
  /** "Deleted user" when the recorded writer was deleted. */
  authorUsername: string | null;
}

export interface ChatSessionDetail extends ChatSession {
  messages: ChatMessage[];
  summary: string | null;
}

export interface ChatSessionsResponse {
  sessions: ChatSession[];
}

export interface ChatMessagesResponse {
  messages: ChatMessage[];
}

// ============================================
// System Types
// ============================================

export interface SystemStatus {
  status: 'ok' | 'degraded';
  timestamp: string;
  environment: string;
  responseTime: string;
  server: {
    uptime: number;
    nodeVersion: string;
    platform: string;
    arch: string;
    memory: {
      total: number;
      free: number;
      used: number;
    };
  };
  dataDirectory: {
    exists: boolean;
    path: string;
    writable: boolean;
    structure: {
      inbox: boolean;
      output: boolean;
      scripts: boolean;
      carriers: boolean;
    };
  };
  claude: {
    available: boolean;
    runtime?: 'cli' | 'agent_sdk';
    version?: string;
    error?: string;
  };
  python: {
    available: boolean;
    version?: string;
    uvAvailable: boolean;
    polarsAvailable: boolean;
    error?: string;
  };
}

export interface HealthStatus {
  status: 'ok';
  timestamp: string;
}

// ============================================
// Fetch Wrapper
// ============================================

type HttpMethod = 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';

interface RequestOptions {
  method?: HttpMethod;
  body?: unknown;
  headers?: Record<string, string>;
  // For file downloads - returns Response instead of parsing JSON
  rawResponse?: boolean;
}

/**
 * Core fetch wrapper with error handling and JSON parsing
 */
async function request<T>(
  endpoint: string,
  options: RequestOptions = {}
): Promise<T> {
  const { method = 'GET', body, headers = {}, rawResponse = false } = options;

  const config: RequestInit = {
    method,
    headers: {
      'Content-Type': 'application/json',
      ...headers,
    },
    // Include cookies for session auth
    credentials: 'include',
  };

  if (body !== undefined) {
    config.body = JSON.stringify(body);
  }

  const response = await fetch(`${API_BASE}${endpoint}`, config);

  // For raw responses (file downloads), return the response directly
  if (rawResponse) {
    if (!response.ok) {
      // Try to parse error response
      try {
        const error = await response.json();
        throw new ApiRequestError({
          error: error.error || 'Request failed',
          message: error.message || 'An error occurred',
          status: response.status,
        });
      } catch (e) {
        if (e instanceof ApiRequestError) throw e;
        throw new ApiRequestError({
          error: 'Request failed',
          message: `HTTP ${response.status}`,
          status: response.status,
        });
      }
    }
    return response as unknown as T;
  }

  const responseText = await response.text();
  let data: unknown = null;
  if (responseText) {
    try {
      data = JSON.parse(responseText);
    } catch {
      if (!response.ok) {
        throw new ApiRequestError({
          error: 'Request failed',
          message: `HTTP ${response.status}`,
          status: response.status,
        });
      }

      throw new ApiRequestError({
        error: 'Invalid response',
        message: 'Server returned a non-JSON response',
        status: response.status,
      });
    }
  }

  if (!response.ok) {
    if (response.status === 401 && typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('telcoos-auth-expired'));
    }

    const errorData = data && typeof data === 'object'
      ? data as {
          error?: unknown;
          message?: unknown;
          clarificationQuestions?: unknown;
          preflight?: unknown;
        }
      : {};

    throw new ApiRequestError({
      error: typeof errorData.error === 'string' ? errorData.error : 'Request failed',
      message: typeof errorData.message === 'string' ? errorData.message : `HTTP ${response.status}`,
      status: response.status,
      clarificationQuestions: Array.isArray(errorData.clarificationQuestions)
        ? errorData.clarificationQuestions.filter((value): value is string => typeof value === 'string')
        : undefined,
      preflight: errorData.preflight && typeof errorData.preflight === 'object'
        ? errorData.preflight as TrafficInvestigationPreflight
        : undefined,
    });
  }

  return data as T;
}

/**
 * Multipart form data request (for file uploads)
 */
async function uploadRequest<T>(
  endpoint: string,
  formData: FormData
): Promise<T> {
  const response = await fetch(`${API_BASE}${endpoint}`, {
    method: 'POST',
    // Don't set Content-Type - browser will set it with boundary for multipart
    credentials: 'include',
    body: formData,
  });

  const responseText = await response.text();
  let data: unknown = null;
  if (responseText) {
    try {
      data = JSON.parse(responseText);
    } catch {
      if (!response.ok) {
        throw new ApiRequestError({
          error: 'Upload failed',
          message: `HTTP ${response.status}`,
          status: response.status,
        });
      }

      throw new ApiRequestError({
        error: 'Invalid response',
        message: 'Server returned a non-JSON response',
        status: response.status,
      });
    }
  }

  if (!response.ok) {
    const errorData = data && typeof data === 'object'
      ? data as { error?: unknown; message?: unknown }
      : {};

    throw new ApiRequestError({
      error: typeof errorData.error === 'string' ? errorData.error : 'Upload failed',
      message: typeof errorData.message === 'string' ? errorData.message : `HTTP ${response.status}`,
      status: response.status,
    });
  }

  return data as T;
}

// ============================================
// Auth API
// ============================================

export const authApi = {
  /**
   * Get current authentication status
   */
  getStatus(): Promise<AuthStatus> {
    return request<AuthStatus>('/auth/status');
  },

  /**
   * Login with username and password
   */
  login(username: string, password: string): Promise<LoginResponse> {
    return request<LoginResponse>('/auth/login', {
      method: 'POST',
      body: { username, password },
    });
  },

  /**
   * Logout and end the session
   */
  logout(): Promise<LogoutResponse> {
    return request<LogoutResponse>('/auth/logout', {
      method: 'POST',
    });
  },

  /**
   * Initial setup - create admin user (only works on fresh instances)
   */
  setup(username: string, password: string): Promise<SetupResponse> {
    return request<SetupResponse>('/auth/setup', {
      method: 'POST',
      body: { username, password },
    });
  },

  /**
   * Change current user's password
   */
  changePassword(currentPassword: string, newPassword: string): Promise<{ success: boolean; message: string }> {
    return request('/auth/change-password', {
      method: 'POST',
      body: { currentPassword, newPassword },
    });
  },

  /**
   * Update current user's display name
   */
  updateDisplayName(displayName: string): Promise<{ success: boolean; displayName: string | null }> {
    return request('/auth/display-name', {
      method: 'PUT',
      body: { displayName },
    });
  },
};

// ============================================
// Files API
// ============================================

export const filesApi = {
  /**
   * Get directory tree structure
   */
  getTree(
    path: string = '',
    options: { depth?: number; flat?: boolean; showHidden?: boolean } = {}
  ): Promise<FileTreeResponse | FileListResponse> {
    const params = new URLSearchParams();
    if (path) params.set('path', path);
    if (options.depth) params.set('depth', String(options.depth));
    if (options.flat) params.set('flat', 'true');
    if (options.showHidden) params.set('showHidden', 'true');
    const query = params.toString();
    return request(`/files/tree${query ? `?${query}` : ''}`);
  },

  /**
   * List directory contents (flat, single level)
   */
  list(
    path: string = '',
    options: { all?: boolean; showHidden?: boolean } = {}
  ): Promise<FileListResponse> {
    const params = new URLSearchParams();
    if (path) params.set('path', path);
    // Both flags map to the server's `all=true` (includes dot-files).
    if (options.all || options.showHidden) params.set('all', 'true');
    const query = params.toString();
    return request(`/files/list${query ? `?${query}` : ''}`);
  },

  /**
   * Get file/directory metadata
   */
  stat(path: string): Promise<FileEntry> {
    return request(`/files/stat?path=${encodeURIComponent(path)}`);
  },

  /**
   * Check if a path exists
   */
  exists(path: string): Promise<FileExistsResponse> {
    return request(`/files/exists?path=${encodeURIComponent(path)}`);
  },

  /**
   * Read file contents as text
   */
  read(
    path: string,
    options: { encoding?: string } = {}
  ): Promise<FileReadResponse> {
    const params = new URLSearchParams({ path });
    if (options.encoding) params.set('encoding', options.encoding);
    return request(`/files/read?${params.toString()}`);
  },

  /**
   * Read one server-bounded Parquet page.
   */
  getParquetPage(
    path: string,
    options: {
      page?: number;
      pageSize?: number;
      filter?: string;
      sortColumn?: string;
      sortDirection?: 'asc' | 'desc';
    } = {}
  ): Promise<ParquetPageResponse> {
    const params = new URLSearchParams({ path });
    if (options.page !== undefined) params.set('page', String(options.page));
    if (options.pageSize !== undefined) params.set('pageSize', String(options.pageSize));
    if (options.filter) params.set('filter', options.filter);
    if (options.sortColumn) params.set('sortColumn', options.sortColumn);
    if (options.sortDirection) params.set('sortDirection', options.sortDirection);
    return request(`/files/parquet?${params.toString()}`);
  },

  /**
   * Get download URL for a file
   * Returns the URL to use for downloading (can be used in <a> tag or window.open)
   */
  getDownloadUrl(path: string, inline: boolean = false): string {
    const params = new URLSearchParams({ path });
    if (inline) params.set('inline', 'true');
    return `${API_BASE}/files/download?${params.toString()}`;
  },

  /**
   * Download a file (returns Response for streaming)
   */
  download(path: string, inline: boolean = false): Promise<Response> {
    const params = new URLSearchParams({ path });
    if (inline) params.set('inline', 'true');
    return request(`/files/download?${params.toString()}`, { rawResponse: true });
  },

  /**
   * Upload files
   */
  upload(
    files: File[],
    destination: string = ''
  ): Promise<UploadResult> {
    const formData = new FormData();
    files.forEach((file) => formData.append('files', file));
    formData.append('destination', destination);
    return uploadRequest('/files/upload', formData);
  },

  /**
   * Move or rename a file/directory
   */
  move(source: string, destination: string): Promise<MoveResult> {
    return request('/files/move', {
      method: 'POST',
      body: { source, destination },
    });
  },

  /**
   * Create a directory
   */
  mkdir(path: string): Promise<MkdirResult> {
    return request('/files/mkdir', {
      method: 'POST',
      body: { path },
    });
  },

  /**
   * Delete a file or directory
   */
  delete(
    path: string,
    options: { recursive?: boolean } = {}
  ): Promise<DeleteResult> {
    const params = new URLSearchParams({ path });
    if (options.recursive) params.set('recursive', 'true');
    return request(`/files?${params.toString()}`, {
      method: 'DELETE',
    });
  },

  /**
   * Create or write a file
   */
  write(path: string, content: string = ''): Promise<WriteResult> {
    return request('/files/write', {
      method: 'POST',
      body: { path, content },
    });
  },
};

// ============================================
// Workspace API
// ============================================

export const workspaceAssetsApi = {
  list(options: { type?: WorkspaceAssetType } = {}): Promise<WorkspaceAssetsResponse> {
    const params = new URLSearchParams();
    if (options.type) params.set('type', options.type);
    const query = params.toString();
    return request(`/workspace/assets${query ? `?${query}` : ''}`);
  },

  get(id: string): Promise<WorkspaceAssetResponse> {
    return request(`/workspace/assets/${encodeURIComponent(id)}`);
  },

  getDownloadUrl(id: string, inline: boolean = false): string {
    const params = new URLSearchParams();
    if (inline) params.set('inline', 'true');
    const query = params.toString();
    return `${API_BASE}/workspace/assets/${encodeURIComponent(id)}/download${query ? `?${query}` : ''}`;
  },

  download(id: string, inline: boolean = false): Promise<Response> {
    const params = new URLSearchParams();
    if (inline) params.set('inline', 'true');
    const query = params.toString();
    return request(`/workspace/assets/${encodeURIComponent(id)}/download${query ? `?${query}` : ''}`, { rawResponse: true });
  },

  delete(id: string): Promise<{ success: boolean; id: string }> {
    return request(`/workspace/assets/${encodeURIComponent(id)}`, {
      method: 'DELETE',
    });
  },

  upload(files: File[], destination: string = ''): Promise<WorkspaceAssetUploadResult> {
    const formData = new FormData();
    files.forEach((file) => formData.append('files', file));
    formData.append('destination', destination);
    return uploadRequest('/workspace/assets/upload', formData);
  },

  reindex(): Promise<{ indexed: number }> {
    return request('/workspace/assets/reindex', { method: 'POST' });
  },

  /**
   * Chunked upload session API. Cloudflare rejects request bodies over roughly
   * 100 MiB before they reach the server, so files above
   * CHUNKED_UPLOAD_THRESHOLD_BYTES upload as bounded chunks through these
   * routes and are assembled server-side into the same workspace asset.
   */
  uploads: {
    createSession(input: {
      filename: string;
      totalSizeBytes: number;
      chunkSizeBytes: number;
      destination: string;
      mimeType?: string | null;
    }): Promise<{ session: UploadSessionState }> {
      return request('/workspace/uploads/sessions', { method: 'POST', body: input });
    },

    getSession(sessionId: string): Promise<{ session: UploadSessionState }> {
      return request(`/workspace/uploads/sessions/${encodeURIComponent(sessionId)}`);
    },

    async putChunk(
      sessionId: string,
      chunkIndex: number,
      blob: Blob
    ): Promise<{ idempotent?: boolean }> {
      const response = await fetch(
        `${API_BASE}/workspace/uploads/sessions/${encodeURIComponent(sessionId)}/chunks/${chunkIndex}`,
        {
          method: 'PUT',
          credentials: 'include',
          headers: { 'Content-Type': 'application/octet-stream' },
          body: blob,
        }
      );

      const responseText = await response.text();
      let data: unknown = null;
      if (responseText) {
        try {
          data = JSON.parse(responseText);
        } catch {
          throw new ApiRequestError({
            error: 'Chunk upload failed',
            message: `HTTP ${response.status}`,
            status: response.status,
          });
        }
      }

      if (!response.ok) {
        const errorData = data && typeof data === 'object'
          ? data as { error?: unknown; message?: unknown }
          : {};
        throw new ApiRequestError({
          error: typeof errorData.error === 'string' ? errorData.error : 'Chunk upload failed',
          message: typeof errorData.message === 'string' ? errorData.message : `HTTP ${response.status}`,
          status: response.status,
        });
      }

      return (data ?? {}) as { idempotent?: boolean };
    },

    complete(sessionId: string): Promise<WorkspaceAssetUploadResult> {
      return request(`/workspace/uploads/sessions/${encodeURIComponent(sessionId)}/complete`, {
        method: 'POST',
      });
    },

    cancel(sessionId: string): Promise<{ cancelled: boolean; sessionId: string }> {
      return request(`/workspace/uploads/sessions/${encodeURIComponent(sessionId)}`, {
        method: 'DELETE',
      });
    },
  },
};

// ============================================
// Chat API
// ============================================

export const chatApi = {
  /**
   * List all chat sessions
   */
  getSessions(): Promise<ChatSessionsResponse> {
    return request('/chat/sessions');
  },

  /**
   * Get a specific session with all messages
   */
  getSession(id: string): Promise<ChatSessionDetail> {
    return request(`/chat/sessions/${encodeURIComponent(id)}`);
  },

  /**
   * Create a new chat session
   */
  createSession(name?: string, provider: 'claude' | 'codex' = 'claude', timezone?: string): Promise<ChatSession> {
    return request('/chat/sessions', {
      method: 'POST',
      body: { ...(name ? { name } : {}), provider, ...(timezone ? { timezone } : {}) },
    });
  },

  /**
   * Update a chat session (rename, set Claude session ID)
   */
  updateSession(
    id: string,
    updates: {
      name?: string;
      provider?: 'claude' | 'codex';
      model?: string | null;
      reasoningEffort?: string | null;
      claudeSessionId?: string;
    }
  ): Promise<ChatSession> {
    return request(`/chat/sessions/${encodeURIComponent(id)}`, {
      method: 'PUT',
      body: updates,
    });
  },

  /**
   * Delete a chat session
   */
  deleteSession(id: string): Promise<{ success: boolean; id: string }> {
    return request(`/chat/sessions/${encodeURIComponent(id)}`, {
      method: 'DELETE',
    });
  },

  /**
   * Add a message to a session
   */
  addMessage(
    sessionId: string,
    role: 'user' | 'assistant' | 'system',
    content: string,
    images?: string[],
    streamId?: string
  ): Promise<ChatMessage> {
    return request(`/chat/sessions/${encodeURIComponent(sessionId)}/messages`, {
      method: 'POST',
      body: { role, content, images, ...(streamId ? { streamId } : {}) },
    });
  },

  /**
   * Get messages for a session
   */
  getMessages(sessionId: string): Promise<ChatMessagesResponse> {
    return request(
      `/chat/sessions/${encodeURIComponent(sessionId)}/messages`
    );
  },

  /**
   * Copy a team chat into a new chat owned by the current user
   */
  forkSession(id: string): Promise<ChatSession> {
    return request(`/chat/sessions/${encodeURIComponent(id)}/fork`, {
      method: 'POST',
    });
  },

  getProduced(sessionId: string): Promise<{ produced: ProducedItem[] }> {
    return request(
      `/chat/sessions/${encodeURIComponent(sessionId)}/produced`
    );
  },
};

export interface ProducedItem {
  source: 'event' | 'window';
  label: string;
  sublabel: string;
  path: string;
  assetId?: string;
  toolName?: string;
  wasPreExisting?: boolean;
  capturedAt: string;
  asset?: {
    id: string;
    title: string;
    type: WorkspaceAssetType;
    sourceFolder: string;
  };
}

// ============================================
// System API
// ============================================

export interface StorageInfo {
  mode: 'shared' | 'per_user';
  used: number;
  usedFormatted: string;
  limit: number;
  limitFormatted: string;
  percentage: number;
}

export interface TenantRuntimeConfig {
  product: {
    key: string;
    name: string;
  };
  tenant: {
    key: string;
    name: string;
  };
  customer: {
    key: string;
    name: string;
  };
  environment: {
    key: string;
    name: string;
  };
  primaryProviderAdapterKey: string;
  primaryProviderAdapter: {
    key: string;
    displayName: string;
    providerName: string;
    integrationType: 'switch_platform';
    mappingStatus: 'unmapped' | 'mapped';
    directApiMapped: boolean;
    browserSessionHelpers: boolean;
  } | null;
  capabilities: {
    storageMode: 'shared' | 'per_user';
    claudeRuntime: 'cli' | 'agent_sdk';
    providerAccess: {
      usesCustomerHeldAccess: boolean;
      browserSessionHelpers: boolean;
      directApiAdapters: boolean;
    };
  };
}

export const systemApi = {
  /**
   * Get public tenant runtime metadata for client branding and capability checks
   */
  getRuntimeConfig(): Promise<TenantRuntimeConfig> {
    return request('/system/runtime-config');
  },

  /**
   * Get detailed system status
   */
  getStatus(): Promise<SystemStatus> {
    return request('/system/status');
  },

  /**
   * Simple health check
   */
  getHealth(): Promise<HealthStatus> {
    return request('/system/health');
  },

  /**
   * Get storage usage
   */
  getStorage(): Promise<StorageInfo> {
    return request('/system/storage');
  },
};

// ============================================
// Switch Connector API
// ============================================

export type SwitchConnectorHealthState =
  | 'not_checked'
  | 'unconfigured'
  | 'ready'
  | 'interaction_required'
  | 'authentication_failed'
  | 'platform_mismatch'
  | 'unavailable';

export interface SwitchConnectorHealth {
  connectorKey: 'sipnav' | 'peeredge' | null;
  state: SwitchConnectorHealthState;
  checkedAt: string;
  code?: string;
  message?: string;
  details?: Record<string, string | number | boolean | null>;
}

export interface SipnavOnboardingPrototypeStart {
  prototype: true;
  jobId: string;
  reviewPath: string;
  carrierCount: number;
  accountCount: number;
  truncated: boolean;
  targetUsername: string;
}

export type SipnavApiMapAvailability =
  | 'available_in_telcoos'
  | 'provider_documented_only';

export interface SipnavApiMapCapability {
  id: string;
  schemaVersion: number;
  effect: 'authentication' | 'provider.read' | 'provider.write';
  title: string;
  description: string;
  timeoutMs?: number;
  operationKeys: string[];
}

export interface SipnavApiMapOperation {
  operationKey: string;
  apiFamilies: string[];
  operationId: string;
  method: string;
  path: string;
  summary: string | null;
  description: string | null;
  parameters: unknown;
  requestBody: unknown;
  requestSchemas: unknown[];
  responses: Array<{ status: string; description: string | null }>;
  responseSchemas: unknown[];
  documentedEffect: string;
  effectRationale: string;
  scope: string;
  verificationState: string;
  verifiedOn: string | null;
  accessibility: string | null;
  observedResponseStructure: unknown | null;
  documentationDifferences: unknown[];
  documentationSource: unknown;
  documentationGaps: string[];
  availability: SipnavApiMapAvailability;
  capabilityIds: string[];
}

export interface SipnavApiMapCatalog {
  provider: 'SipNav';
  source: {
    snapshot: string;
    sha256: string;
    capturedOn: string;
    exportUrl: string;
  };
  counts: {
    operations: number;
    providerReads: number;
    deferredNonReads: number;
    availableInTelcoOS: number;
    providerDocumentedOnly: number;
    capabilities: number;
  };
  capabilities: SipnavApiMapCapability[];
  operations: SipnavApiMapOperation[];
}

export const switchConnectorsApi = {
  getStatus(): Promise<SwitchConnectorHealth> {
    return request('/connectors/status');
  },

  probe(): Promise<SwitchConnectorHealth> {
    return request('/connectors/probe', { method: 'POST' });
  },

  startSipnavOnboardingPrototype(): Promise<SipnavOnboardingPrototypeStart> {
    return request('/connectors/sipnav/onboarding-prototype', { method: 'POST' });
  },

  getSipnavApiMap(): Promise<SipnavApiMapCatalog> {
    return request('/connectors/sipnav/api-map');
  },
};

// ============================================
// Carrier Initialization API
// ============================================

export type CarrierInitializationDiscoveryPhase =
  | 'account_discovery'
  | 'vendor_discovery'
  | 'complete';

export type CarrierInitializationDiscoveryStatus =
  | 'pending'
  | 'progress'
  | 'rate_limited'
  | 'complete';

export interface CarrierInitializationDiscoveryProgress {
  phase: CarrierInitializationDiscoveryPhase;
  status: CarrierInitializationDiscoveryStatus;
  counts: {
    accounts: number;
    vendors: number;
    assignmentEvidence: number;
    unresolvedAssignmentEvidence: number;
  };
  truncated: boolean;
  complete: boolean;
}

export interface CarrierInitializationDiscoveryState {
  jobId: string;
  jobRevision: number;
  progress: CarrierInitializationDiscoveryProgress;
}

export interface AdvancedCarrierInitializationDiscovery
  extends CarrierInitializationDiscoveryState {
  created: boolean;
  advancedPhase: Exclude<CarrierInitializationDiscoveryPhase, 'complete'> | null;
}

export interface CarrierInitializationDiscoveryAccount {
  providerAccountId: number;
  name: string;
  enabled: boolean | null;
  company: { id: number; name: string };
  evidence: Array<'catalog' | 'routing_assignment' | 'rate_deck_assignment'>;
}

export interface CarrierInitializationDiscoveryVendor {
  providerCarrierId: number;
  name: string;
  enabled: boolean | null;
  relatedAccountIds: number[];
}

export interface CarrierInitializationDiscoveryReview {
  jobId: string;
  jobRevision: number;
  accounts: CarrierInitializationDiscoveryAccount[];
  vendors: CarrierInitializationDiscoveryVendor[];
  sourceComplete: boolean;
  sourceTruncated: boolean;
  hasMoreAccounts: boolean;
  hasMoreVendors: boolean;
}

export type CarrierInitializationMappingBlockReason =
  | 'discovery_incomplete'
  | 'source_truncated'
  | 'group_limit';

export type CarrierInitializationCarrierReviewStatus =
  | 'not_required'
  | 'needs_review'
  | 'confirmed';

export interface CarrierInitializationCarrierDraft {
  id: string;
  name: string;
  folderPath: string;
  updatedAt: string;
  reviewStatus: CarrierInitializationCarrierReviewStatus;
  reviewedBy: { id: number; username: string } | null;
  reviewedAt: string | null;
  assignedAccounts: CarrierInitializationAccountSummary[];
  assignedVendors: CarrierInitializationAssignedVendorSummary[];
}

export interface CarrierInitializationAccountSummary {
  providerAccountId: number;
  name: string;
  enabled: boolean | null;
  company: { id: number; name: string };
  serviceDefinitionId?: string;
  proposedService?: CarrierTrunkServiceProposal;
}

// A release 1 proposal (ADR-0024): shown in the Organizer, applied only when an
// administrator accepts it. serviceDefinitionId null means a hint only.
export interface CarrierTrunkServiceProposal {
  serviceDefinitionId: string | null;
  confidence: 'medium' | 'low';
  reason: string;
}

export interface CarrierInitializationVendorSummary {
  providerCarrierId: number;
  name: string;
  enabled: boolean | null;
  serviceDefinitionId?: string;
  proposedService?: CarrierTrunkServiceProposal;
}

export type CarrierInitializationVendorAssignmentSource =
  | 'manual'
  | 'name_inference';

export interface CarrierInitializationAssignedVendorSummary
  extends CarrierInitializationVendorSummary {
  assignmentSource: CarrierInitializationVendorAssignmentSource;
}

export interface CarrierInitializationUnassignedVendor
  extends CarrierInitializationVendorSummary {
  suggestedProviderCompanyId: number | null;
  proposedProviderCompanyId?: number;
  proposedCarrierDraftId?: string;
  proposedNewCarrierName?: string;
  proposalConfidence?: 'medium';
  proposalReason?: string;
}

export type CarrierRecordType = 'customer_company' | 'customer_account' | 'vendor_carrier';
export type CarrierRecordIgnoreReason =
  | 'decommissioned'
  | 'test_or_internal'
  | 'duplicate'
  | 'not_a_carrier'
  | 'other';

export interface CarrierInitializationIgnoredRecord {
  recordType: CarrierRecordType;
  providerRecordId: number;
  name: string;
  providerNameAtDecision: string;
  reason: CarrierRecordIgnoreReason;
  ignoredBy: { id: number; username: string };
  ignoredAt: string;
  present: boolean;
  reviewRequired: boolean;
}

export interface CarrierInitializationCompanyGroup {
  providerCompanyId: number;
  providerCompanyName: string;
  accountCount: number;
  accounts: CarrierInitializationAccountSummary[];
  sampleAccounts: Array<{
    providerAccountId: number;
    name: string;
  }>;
}

export interface CarrierInitializationMappingDrafts {
  jobId: string;
  jobRevision: number;
  ready: boolean;
  blockedReason: CarrierInitializationMappingBlockReason | null;
  serviceDefinitions: ServiceDefinitionFormValue[];
  groups: CarrierInitializationCompanyGroup[];
  drafts: CarrierInitializationCarrierDraft[];
  unassignedAccounts: CarrierInitializationAccountSummary[];
  unassignedVendors: CarrierInitializationUnassignedVendor[];
  ignoredRecords: CarrierInitializationIgnoredRecord[];
}

export interface CarrierInitializationServiceDefinitionsState {
  jobId: string;
  jobRevision: number;
  definitions: ServiceDefinitionFormValue[];
}

export type DomesticServiceRateSummaryStatus =
  | 'ready'
  | 'warning'
  | 'unavailable'
  | 'conflict'
  | 'no_data'
  | 'failed';

export type DomesticServiceRateSummaryWarning =
  | 'negative_rate'
  | 'zero_rate'
  | 'missing_rates'
  | 'excluded_non_npanxx_rows'
  | 'deck_unavailable'
  | 'effective_version_conflict'
  | 'rate_rows_conflict'
  | 'no_domestic_npanxx'
  | 'summary_failed';

export interface DomesticServiceRateSummaryRate {
  average: string | null;
  numericCount: number;
}

export interface DomesticServiceRateSummaryRow {
  carrierId: string;
  carrierName: string;
  connectorKey: string;
  providerRecordId: number;
  serviceDefinitionId: string;
  providerDeckId: number | null;
  versionId: string | null;
  effectiveStart: string | null;
  effectiveEnd: string | null;
  synchronizedAt: string | null;
  npanxxCount: number;
  excludedRowCount: number;
  negativeRateCount: number;
  zeroRateCount: number;
  rates: {
    interstate: DomesticServiceRateSummaryRate;
    intrastate: DomesticServiceRateSummaryRate;
    indeterminate: DomesticServiceRateSummaryRate;
  };
  status: DomesticServiceRateSummaryStatus;
  warnings: DomesticServiceRateSummaryWarning[];
}

export interface DomesticServiceRateSummary {
  serviceDefinitionId: string;
  providerCalendarEffectiveAt: string;
  direction: 'inbound' | 'outbound';
  weighting: 'unweighted_distinct_npanxx';
  omittedNonApplicableCount: number;
  rows: DomesticServiceRateSummaryRow[];
}

export interface TrafficPerformanceTotals {
  inCount: number;
  outCount: number;
  completedCount: number;
  interstateMinutes: number;
  intrastateMinutes: number;
  ijMinutes: number;
  shortDurationCount: number;
  failoverCount: number;
}

export type TrafficSipResponseCode =
  | '403'
  | '404'
  | '408'
  | '480'
  | '481'
  | '486'
  | '487'
  | '500'
  | '503'
  | '510'
  | '52x'
  | '53x'
  | '603'
  | '604'
  | 'other';

export type TrafficSipResponseCounts = Record<TrafficSipResponseCode, number>;

export interface TrafficPerformanceRow extends TrafficPerformanceTotals {
  carrierId: string;
  carrierName: string;
  providerRecordId: number;
  providerRecordName: string;
  sipResponseCounts: TrafficSipResponseCounts;
  topFailureCode: string | null;
  topFailureCount: number;
}

export interface TrafficPerformancePartyReport {
  mappedTrunkCount: number;
  observedTrunkCount: number;
  rows: TrafficPerformanceRow[];
  totals: TrafficPerformanceTotals;
  truncated: boolean;
}

export interface DomesticTrafficPerformanceReport {
  serviceDefinitionId: string;
  providerCalendarStartDate: string;
  providerCalendarEndDate: string;
  source: {
    connectorKey: 'sipnav';
    dataset: 'aggregate_master_statistics';
    cdrRequested: false;
    providerResponsePersisted: false;
  };
  customers: TrafficPerformancePartyReport;
  providers: TrafficPerformancePartyReport;
  warnings: Array<
    | 'provider_timezone_unverified'
    | 'financial_fields_excluded'
    | 'ij_minutes_semantics_unverified'
  >;
}

export interface TrafficMetricDelta {
  current: number;
  comparison: number;
  absoluteChange: number;
  percentChange: number | null;
}

export interface TrafficInvestigationPeriod {
  startDate: string;
  endDate: string;
}

export type TrafficInvestigationDraftState =
  | 'incomplete'
  | 'ready'
  | 'collecting'
  | 'completed'
  | 'failed'
  | 'cancelled';

export interface TrafficInvestigationDraft {
  id: string;
  tenantKey: string;
  originatingChatSessionId: string;
  initiatingUserId: number;
  originalQuestion: string;
  concerns: string[];
  serviceDefinitionId: string | null;
  currentPeriod: TrafficInvestigationPeriod | null;
  comparisonPeriod: TrafficInvestigationPeriod | null;
  dateResolutionBasis: string | null;
  providerCalendarTimezone: string | null;
  missingFields: string[];
  revision: number;
  state: TrafficInvestigationDraftState;
  latestFailureCode: string | null;
  evidenceRunId: string | null;
  caseId: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface TrafficInvestigationPreflight {
  draftId: string;
  draftRevision: number;
  mappingSnapshotId: string;
  serviceDefinitionId: string;
  periods: {
    current: TrafficInvestigationPeriod;
    comparison: TrafficInvestigationPeriod;
  };
  requestedRecordCounts: { customers: number; providers: number };
  providerRecordIdsPerRequest: 10;
  maximumRecordCounts: { customers: 256; providers: 256 };
  estimatedRequestCount: number;
  maximumRequestCount: 104;
  allowed: boolean;
  rejection: TrafficEvidenceFailure | null;
}

export interface TrafficInvestigationPreflightResponse {
  draft: TrafficInvestigationDraft;
  preflight: TrafficInvestigationPreflight;
}

export type TrafficEvidenceRunStatus =
  | 'pending'
  | 'running'
  | 'complete'
  | 'failed'
  | 'interrupted'
  | 'cancelled';

export type TrafficEvidenceCompleteness = 'pending' | 'complete' | 'incomplete';

export interface TrafficEvidenceFailure {
  code: string;
  message: string;
}

export interface TrafficEvidenceAttempt {
  id: string;
  runId: string;
  periodKind: 'current' | 'comparison';
  partyType: 'customer' | 'provider';
  batchIndex: number;
  deterministicBatchId: string;
  providerRecordIds: number[];
  attemptNumber: number;
  outcome: 'pending' | 'succeeded' | 'failed' | 'interrupted';
  failure: TrafficEvidenceFailure | null;
  startedAt: string;
  completedAt: string | null;
}

export interface TrafficEvidenceSource {
  connectorKey: 'sipnav';
  capabilityId: 'sipnav.traffic-performance.read.v1';
  dataset: 'aggregate_master_statistics';
  invocationContractVersion: number;
  evidenceSchemaVersion: 1;
  sourceRevision: string;
  cdrRequested: false;
  rawProviderResponsePersisted: false;
}

export interface TrafficEvidenceRun {
  id: string;
  tenantKey: string;
  draftId: string;
  draftRevision: number;
  mappingSnapshotId: string;
  initiatingActor: { userId: number; username: string };
  originatingChatSessionId: string;
  requestCorrelationId: string;
  serviceDefinitionId: string;
  periods: {
    current: TrafficInvestigationPeriod;
    comparison: TrafficInvestigationPeriod;
  };
  source: TrafficEvidenceSource;
  requestedRecordCounts: { customers: number; providers: number };
  estimatedRequestCount: number;
  actualRequestCount: number;
  retryCount: number;
  status: TrafficEvidenceRunStatus;
  completeness: TrafficEvidenceCompleteness;
  startedAt: string | null;
  completedAt: string | null;
  createdAt: string;
  warnings: string[];
  failure: TrafficEvidenceFailure | null;
  attempts: TrafficEvidenceAttempt[];
  caseId: string | null;
}

export interface TrafficInvestigationRunResponse {
  draft: TrafficInvestigationDraft;
  run: TrafficEvidenceRun;
  case: TrafficInvestigationCase | null;
  surfaceMarker: string;
}

export interface TrafficInvestigationDraftResponse {
  draft: TrafficInvestigationDraft;
  preflight: TrafficInvestigationPreflight;
  latestRun: TrafficEvidenceRun | null;
}

export interface TrafficInvestigationRunsResponse {
  runs: TrafficEvidenceRun[];
}

export interface TrafficInvestigationPartyComparison {
  mappedTrunkCount: TrafficMetricDelta;
  observedTrunkCount: TrafficMetricDelta;
  incomingCount: TrafficMetricDelta;
  outgoingCount: TrafficMetricDelta;
  completedCount: TrafficMetricDelta;
  shortDurationCount: TrafficMetricDelta;
  failoverCount: TrafficMetricDelta;
  sipResponses: Array<{ code: string; counts: TrafficMetricDelta }>;
}

export interface TrafficEvidenceFinding {
  statement: string;
  evidenceKeys: string[];
}

export interface TrafficInvestigationSubscriptionComparison {
  carrierId: string;
  carrierName: string;
  providerRecordId: number;
  providerRecordName: string;
  current: TrafficPerformanceRow | null;
  comparison: TrafficPerformanceRow | null;
}

export type TrafficInvestigationAttribution =
  | {
      status: 'attributed';
      draftId: string;
      draftRevision: number;
      runId: string;
      completeness: 'complete';
      source: TrafficEvidenceSource;
      requestedRecordCounts: { customers: number; providers: number };
      estimatedRequestCount: number;
      actualRequestCount: number;
      retryCount: number;
      capturedAt: string;
    }
  | {
      status: 'legacy';
      draftId: null;
      draftRevision: null;
      runId: null;
      completeness: 'unavailable';
      source: null;
      requestedRecordCounts: null;
      estimatedRequestCount: null;
      actualRequestCount: null;
      retryCount: null;
      capturedAt: null;
    };

export interface TrafficInvestigationCase {
  id: string;
  status: 'proposed' | 'confirmed';
  serviceDefinitionId: string;
  question: string;
  currentPeriod: TrafficInvestigationPeriod;
  comparisonPeriod: TrafficInvestigationPeriod;
  explanation: string;
  supportingEvidence: TrafficEvidenceFinding[];
  contradictingEvidence: TrafficEvidenceFinding[];
  missingEvidence: string[];
  comparisons: {
    customers: TrafficInvestigationPartyComparison;
    providers: TrafficInvestigationPartyComparison;
  };
  subscriptions: {
    customers: TrafficInvestigationSubscriptionComparison[];
    providers: TrafficInvestigationSubscriptionComparison[];
  };
  source: {
    connectorKey: 'sipnav';
    dataset: 'aggregate_master_statistics';
    cdrRequested: false;
    rawProviderResponsePersisted: false;
    routingPolicyRead: false;
    routingExecutionRead: false;
  };
  createdBy: { userId: number; username: string };
  confirmedBy: { userId: number; username: string; confirmedAt: string } | null;
  feedbackCount: number;
  attribution: TrafficInvestigationAttribution;
  createdAt: string;
  updatedAt: string;
}

export type CarrierFolderPublicationBlockReason =
  | 'mapping_incomplete'
  | 'mapping_integrity_error'
  | 'audit_running'
  | 'audit_conflict'
  | 'audit_review_pending'
  | 'disposition_incomplete'
  | 'review_required';

export interface CarrierMappingDisposition {
  unassignedAccounts: number;
  unassignedVendors: number;
  linkedWithoutServiceType: number;
  orphanLinks: number;
}

export interface CarrierFolderPublicationProgress {
  jobId: string;
  jobRevision: number;
  ready: boolean;
  blockedReason: CarrierFolderPublicationBlockReason | null;
  foldersCurrent: boolean;
  disposition: CarrierMappingDisposition;
  totalCarriers: number;
  currentCarriers: number;
  pendingCarriers: number;
  trafficHistoryEffectiveDate: {
    status: 'not_required' | 'missing' | 'current' | 'stale';
    providerCalendarDate: string | null;
    selectedBy: { id: number; username: string } | null;
    selectedAt: string | null;
  };
}

export interface AdvancedCarrierFolderPublication
  extends CarrierFolderPublicationProgress {
  published: boolean;
  carrier: {
    id: string;
    name: string;
    folderPath: string;
  } | null;
}

export type CarrierAuditOutcome =
  | 'matched'
  | 'renamed'
  | 'new'
  | 'missing'
  | 'conflict'
  | 'ignored';

export interface CarrierAuditRecord {
  connectorKey: string;
  recordType: 'customer_account' | 'vendor_carrier';
  direction: 'inbound' | 'outbound';
  providerRecordId: number;
  baselineProviderName: string | null;
  providerName: string | null;
  providerCompanyId: number | null;
  providerCompanyName: string | null;
  enabled: boolean | null;
  carrierId: string | null;
  carrierName: string | null;
  observedCount: number;
  conflictReason: string | null;
  outcome: CarrierAuditOutcome | null;
}

export interface CarrierAuditDispatchState {
  status: 'running' | 'paused' | 'complete';
  lastErrorCode: string | null;
  chatSessionId: string;
}

export interface CarrierAuditState extends CarrierAuditProgress {
  dispatch: CarrierAuditDispatchState | null;
}

export interface CarrierAuditProgress {
  runId: string;
  jobId: string;
  jobRevision: number;
  status: 'running' | 'complete' | 'superseded';
  phase: 'account_discovery' | 'vendor_discovery' | 'classifying' | 'complete';
  counts: Record<CarrierAuditOutcome, number>;
  observedAccounts: number;
  observedVendors: number;
  records: CarrierAuditRecord[];
  hasMoreRecords: boolean;
  reviewPreparedJobRevision: number | null;
  reviewPreparedAt: string | null;
  startedAt: string;
  updatedAt: string;
  completedAt: string | null;
}

export const carrierInitializationApi = {
  getServiceDefinitions(): Promise<CarrierInitializationServiceDefinitionsState | null> {
    return request('/carrier-initialization/service-definitions');
  },

  getDiscovery(): Promise<CarrierInitializationDiscoveryState | null> {
    return request('/carrier-initialization/discovery');
  },

  advanceDiscovery(): Promise<AdvancedCarrierInitializationDiscovery> {
    return request('/carrier-initialization/discovery/advance', { method: 'POST' });
  },

  getDiscoveryReview(): Promise<CarrierInitializationDiscoveryReview | null> {
    return request('/carrier-initialization/discovery/review');
  },

  async getMappingDrafts(): Promise<CarrierInitializationMappingDrafts | null> {
    return parseCarrierInitializationMappingDraftsResponse(
      await request<unknown>('/carrier-initialization/mapping-drafts')
    );
  },

  async replaceMappingDrafts(input: {
    jobId: string;
    expectedJobRevision: number;
    drafts: Array<{
      id?: string;
      name: string;
      providerAccountIds: number[];
      providerVendorIds: number[];
      accountServiceAssignments?: Array<{
        providerAccountId: number;
        serviceDefinitionId: string | null;
      }>;
      vendorServiceAssignments?: Array<{
        providerCarrierId: number;
        serviceDefinitionId: string | null;
      }>;
      reviewStatus?: CarrierInitializationCarrierReviewStatus;
      reviewNow?: boolean;
    }>;
  }): Promise<CarrierInitializationMappingDrafts> {
    const response = await request<unknown>('/carrier-initialization/mapping-drafts', {
      method: 'PUT',
      body: input,
    });
    return parseCarrierInitializationMappingDraftsResponse(response)!;
  },

  async changeRecordDisposition(input: {
    jobId: string;
    expectedJobRevision: number;
    recordType: CarrierRecordType;
    providerRecordId: number;
    action: 'ignore' | 'restore';
    reason?: CarrierRecordIgnoreReason;
  }): Promise<CarrierInitializationMappingDrafts> {
    const response = await request<unknown>('/carrier-initialization/mapping-dispositions', {
      method: 'PUT',
      body: input,
    });
    return parseCarrierInitializationMappingDraftsResponse(response)!;
  },

  getFolderPublication(): Promise<CarrierFolderPublicationProgress | null> {
    return request('/carrier-initialization/folder-publication');
  },

  advanceFolderPublication(): Promise<AdvancedCarrierFolderPublication | null> {
    return request('/carrier-initialization/folder-publication/advance', {
      method: 'POST',
    });
  },

  saveTrafficHistoryEffectiveDate(
    providerCalendarDate: string
  ): Promise<CarrierFolderPublicationProgress> {
    return request('/carrier-initialization/folder-publication/effective-date', {
      method: 'PUT',
      body: { providerCalendarDate },
    });
  },

  getCarrierAudit(): Promise<CarrierAuditState | null> {
    return request('/carrier-initialization/carrier-audit');
  },

  startCarrierAudit(chatSessionId: string): Promise<{ dispatch: CarrierAuditDispatchState }> {
    return request('/carrier-initialization/carrier-audit/start', {
      method: 'POST',
      body: { chatSessionId },
    });
  },

  prepareCarrierAuditReview(runId: string): Promise<CarrierAuditProgress> {
    return request('/carrier-initialization/carrier-audit/prepare-review', {
      method: 'POST',
      body: { runId },
    });
  },
};

// ============================================
// Admin Types
// ============================================

export interface AdminUser {
  id: number;
  username: string;
  isAdmin: boolean;
  isSupportAdmin: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface AdminUsersResponse {
  users: AdminUser[];
}

export interface CreateUserRequest {
  username: string;
  password: string;
  isAdmin?: boolean;
}

export interface ResetPasswordRequest {
  password: string;
}

export type TrainingMemoryStatus = 'active' | 'proposed' | 'archived';
export type TrainingMemoryCategory = 'role' | 'workflow' | 'policy' | 'definition' | 'preference' | 'fact' | 'decision' | 'note';
export type TrainingMemorySource = 'user' | 'agent' | 'support_admin' | 'seeded';

export interface TrainingMemory {
  id: string;
  status: TrainingMemoryStatus;
  category: TrainingMemoryCategory;
  source: TrainingMemorySource;
  title: string;
  content: string;
  appliesWhen: string[];
  provenance: string[];
  createdBy: {
    userId: number | null;
    username: string | null;
  };
  archivedAt: string | null;
  archiveReason: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface TrainingMemoryEvent {
  id: string;
  memoryId: string | null;
  type: 'created' | 'activated' | 'archived' | 'restored' | 'materialized';
  actor: {
    userId: number | null;
    username: string | null;
  };
  summary: string;
  payload: unknown;
  createdAt: string;
}

export interface TrainingMemoryResponse {
  memories: TrainingMemory[];
  events: TrainingMemoryEvent[];
}

export interface CreateTrainingMemoryRequest {
  title: string;
  content: string;
  category?: TrainingMemoryCategory;
  source?: TrainingMemorySource;
  status?: TrainingMemoryStatus;
  appliesWhen?: string[];
  provenance?: string[];
}

export interface LearningMemory {
  id: string;
  scope: 'user' | 'tenant';
  ownerUserId: number | null;
  ownerUsername: string | null;
  memoryKey: string;
  status: TrainingMemoryStatus;
  category: TrainingMemoryCategory | 'fact' | 'decision';
  source: TrainingMemorySource;
  title: string;
  content: string;
  supersedesMemoryId: string | null;
  conflictGroupId: string | null;
  sourceChatSessionId: string | null;
  sourceMessageIds: number[];
  sourceStreamId: string | null;
  sourceProvider: string | null;
  sourceModel: string | null;
  learningRunId: string | null;
  expiresAt: string | null;
  reviewAfter: string | null;
  reviewDue: boolean;
  createdByUsername: string | null;
  archivedAt: string | null;
  archiveReason: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface LearningMemoryView {
  myMemory: LearningMemory[];
  tenantMemory: LearningMemory[];
  events: Array<{
    id: string;
    memoryId: string | null;
    type: string;
    actorUsername: string | null;
    learningRunId: string | null;
    reviewerProvider: string | null;
    reviewerModel: string | null;
    summary: string;
    createdAt: string;
  }>;
  capacity: {
    tenant: MemoryCapacityUsage;
    user: MemoryCapacityUsage;
  };
  permissions: {
    manageOwnMemory: boolean;
    manageTenantMemory: boolean;
    viewRecommendations: boolean;
    viewAllUserMemory: boolean;
  };
}

export interface MemoryCapacityUsage {
  usedEntries: number;
  maxEntries: number;
  usedChars: number;
  maxChars: number;
  reviewRequired: boolean;
  consolidationStatus: 'not_needed' | 'review_required';
  consolidationRequestId: string | null;
  consolidationRequestedAt: string | null;
  consolidationReview: {
    requiredEntryDelta: number;
    requiredCharDelta: number;
    projectedEntries: number;
    projectedChars: number;
    backupEntryCount: number;
  } | null;
}

export interface TenantSkillRevision {
  id: string;
  version: number;
  markdownContent: string;
  baseVersionObserved: string | null;
  baseContentHash: string | null;
  sourceLearningRunId: string | null;
  sourceMessageIds: number[];
  reviewerProvider: string | null;
  reviewerModel: string | null;
  supersedesRevisionId: string | null;
  contentHash: string;
  createdByUsername: string | null;
  createdAt: string;
}

export interface TenantSkillLearning {
  id: string;
  skillName: string;
  mode: 'overlay' | 'standalone';
  baseShippedSkillName: string | null;
  baseState: 'present' | 'removed' | null;
  activeRevisionId: string | null;
  status: 'active' | 'archived' | 'review_due';
  pinned: boolean;
  revisionCount: number;
  activeRevision: TenantSkillRevision | null;
  revisions: TenantSkillRevision[];
  health: {
    explicitInvocationCount: number;
    lastExplicitInvocationAt: string | null;
    materializationCount: number;
  };
  createdAt: string;
  updatedAt: string;
}

export interface TenantSkillLearningView {
  skills: TenantSkillLearning[];
  events: Array<{
    id: string;
    tenantSkillId: string;
    revisionId: string | null;
    type: string;
    actorUsername: string | null;
    learningRunId: string | null;
    summary: string;
    createdAt: string;
  }>;
  permissions: { manageTenantSkills: boolean };
}

export type ShippedSkillRecommendationStatus =
  | 'new'
  | 'watching'
  | 'accepted_for_dev'
  | 'dismissed_tenant_specific'
  | 'dismissed_incorrect'
  | 'resolved_by_release';

export interface ShippedSkillRecommendation {
  id: string;
  skillName: string;
  shippedVersion: string;
  shippedExcerpt: string;
  ownerKind: 'global_common' | 'connector';
  connectorKey: string | null;
  bundleId: string;
  runtimeMetadata: Record<string, unknown>;
  category: 'incorrect_instruction' | 'missing_diagnostic' | 'stale_capability' | 'interpretation_gap' | 'other';
  summary: string;
  rationale: string;
  sourceLearningRunId: string;
  sourceChatSessionId: string;
  tenantSkillRevisionId: string | null;
  tenantSkillRevisionMarkdown: string | null;
  generalizedDiff: string;
  sourceMessageIds: number[];
  reviewerProvider: string | null;
  reviewerModel: string | null;
  status: ShippedSkillRecommendationStatus;
  evidenceCount: number;
  mostRecentEvidenceAt: string;
  supportNote: string | null;
  statusChangedByUsername: string | null;
  statusChangedAt: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface ShippedSkillRecommendationInbox {
  recommendations: ShippedSkillRecommendation[];
  events: Array<{
    id: string;
    recommendationId: string;
    type: string;
    actorUsername: string | null;
    learningRunId: string | null;
    beforeStatus: string | null;
    afterStatus: string | null;
    note: string | null;
    summary: string;
    createdAt: string;
  }>;
  newCount: number;
}

export interface ShippedSkillRecommendationSource {
  evidenceSets: Array<{
    chatSessionId: string;
    chatName: string;
    learningRunId: string;
    messages: Array<{
      id: number;
      role: 'user' | 'assistant' | 'system';
      content: string;
      truncated: boolean;
    }>;
  }>;
}

export interface LearningRunDiagnostics {
  counts: Record<'pending' | 'running' | 'succeeded' | 'failed' | 'skipped', number>;
  runs: Array<{
    id: string;
    streamId: string;
    chatSessionId: string;
    userId: number;
    provider: string;
    model: string | null;
    reviewerProvider: string | null;
    reviewerModel: string | null;
    status: 'pending' | 'running' | 'succeeded' | 'failed' | 'skipped';
    errorCode: string | null;
    attemptCount: number;
    maxAttempts: number;
    priority: number;
    createdAt: string;
    startedAt: string | null;
    completedAt: string | null;
    updatedAt: string;
  }>;
}

export const learningApi = {
  getMemory(): Promise<LearningMemoryView> {
    return request('/learning/memory');
  },

  createMemory(input: {
    scope: 'user' | 'tenant';
    memoryKey: string;
    category: TrainingMemoryCategory;
    title: string;
    content: string;
  }): Promise<{ memory: LearningMemory }> {
    return request('/learning/memory', { method: 'POST', body: input });
  },

  correctMemory(id: string, input: { title: string; content: string }): Promise<{ memory: LearningMemory }> {
    return request(`/learning/memory/${encodeURIComponent(id)}/correct`, {
      method: 'POST',
      body: input,
    });
  },

  setMemoryLifecycle(id: string, status: 'active' | 'archived', reason?: string): Promise<{ memory: LearningMemory }> {
    return request(`/learning/memory/${encodeURIComponent(id)}/lifecycle`, {
      method: 'POST',
      body: { status, reason },
    });
  },

  rollbackMemory(id: string): Promise<{ memory: LearningMemory }> {
    return request(`/learning/memory/${encodeURIComponent(id)}/rollback`, { method: 'POST' });
  },

  resolveMemoryConflict(id: string): Promise<{ memory: LearningMemory }> {
    return request(`/learning/memory/${encodeURIComponent(id)}/resolve-conflict`, { method: 'POST' });
  },

  resolveMemoryConsolidationReview(scope: 'tenant' | 'user'): Promise<{ success: boolean }> {
    return request(`/learning/memory/consolidation/${scope}/resolve`, { method: 'POST' });
  },

  getTenantSkills(): Promise<TenantSkillLearningView> {
    return request('/learning/skills');
  },

  setTenantSkillLifecycle(id: string, status: 'active' | 'archived'): Promise<{ skill: TenantSkillLearning }> {
    return request(`/learning/skills/${encodeURIComponent(id)}/lifecycle`, {
      method: 'POST',
      body: { status },
    });
  },

  rollbackTenantSkill(id: string, revisionId: string): Promise<{ skill: TenantSkillLearning }> {
    return request(`/learning/skills/${encodeURIComponent(id)}/rollback`, {
      method: 'POST',
      body: { revisionId },
    });
  },

  setTenantSkillPinned(id: string, pinned: boolean): Promise<{ skill: TenantSkillLearning }> {
    return request(`/learning/skills/${encodeURIComponent(id)}/pin`, {
      method: 'POST',
      body: { pinned },
    });
  },

  acknowledgeTenantSkillReview(id: string): Promise<{ skill: TenantSkillLearning }> {
    return request(`/learning/skills/${encodeURIComponent(id)}/acknowledge-review`, {
      method: 'POST',
    });
  },

  getRecommendations(): Promise<ShippedSkillRecommendationInbox> {
    return request('/learning/recommendations');
  },

  getRecommendationSource(id: string): Promise<{ source: ShippedSkillRecommendationSource }> {
    return request(`/learning/recommendations/${encodeURIComponent(id)}/source`);
  },

  getDiagnostics(): Promise<LearningRunDiagnostics> {
    return request('/learning/diagnostics');
  },

  setRecommendationStatus(
    id: string,
    status: ShippedSkillRecommendationStatus,
    note?: string
  ): Promise<{ recommendation: ShippedSkillRecommendation }> {
    return request(`/learning/recommendations/${encodeURIComponent(id)}/status`, {
      method: 'POST',
      body: { status, note },
    });
  },
};

// ============================================
// Admin API
// ============================================

export const adminApi = {
  /**
   * List all users (admin only)
   */
  getUsers(): Promise<AdminUsersResponse> {
    return request('/admin/users');
  },

  /**
   * Create a new user (admin only)
   */
  createUser(data: CreateUserRequest): Promise<AdminUser> {
    return request('/admin/users', {
      method: 'POST',
      body: data,
    });
  },

  /**
   * Delete a user (admin only)
   */
  deleteUser(id: number): Promise<{ success: boolean; id: number }> {
    return request(`/admin/users/${id}`, {
      method: 'DELETE',
    });
  },

  /**
   * Reset a user's password (admin only)
   */
  resetPassword(id: number, password: string): Promise<{ success: boolean; id: number }> {
    return request(`/admin/users/${id}/reset-password`, {
      method: 'POST',
      body: { password },
    });
  },

  /**
   * Reset a user's Claude credentials (admin only)
   * Removes .claude/ directory and .claude.json, forcing re-authentication
   */
  resetClaudeCredentials(id: number): Promise<{ success: boolean; id: number }> {
    return request(`/admin/users/${id}/reset-claude-credentials`, {
      method: 'POST',
    });
  },

  getTrainingMemory(status?: TrainingMemoryStatus): Promise<TrainingMemoryResponse> {
    const params = new URLSearchParams();
    if (status) params.set('status', status);
    const query = params.toString();
    return request(`/admin/training-memory${query ? `?${query}` : ''}`);
  },

  createTrainingMemory(data: CreateTrainingMemoryRequest): Promise<{ memory: TrainingMemory }> {
    return request('/admin/training-memory', {
      method: 'POST',
      body: data,
    });
  },

  updateTrainingMemoryStatus(
    id: string,
    status: TrainingMemoryStatus,
    reason?: string
  ): Promise<{ memory: TrainingMemory }> {
    return request(`/admin/training-memory/${encodeURIComponent(id)}/status`, {
      method: 'POST',
      body: { status, reason },
    });
  },

  materializeTrainingMemory(): Promise<{ success: boolean; path: string; activeCount: number }> {
    return request('/admin/training-memory/materialize', {
      method: 'POST',
    });
  },

  /**
   * Upload a skill folder to shared skills (admin only)
   */
  async uploadSkillFolder(files: FileList): Promise<{ success: boolean; skill: { name: string; title: string; description: string } }> {
    const formData = new FormData();

    // Get the skill name from the first file's path (folder name)
    const firstFile = files[0];
    const pathParts = firstFile.webkitRelativePath.split('/');
    const skillName = pathParts[0];
    formData.append('skillName', skillName);

    // Add all files with their relative paths
    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      // Get path relative to the skill folder (remove the folder name prefix)
      const relativePath = file.webkitRelativePath.split('/').slice(1).join('/');
      formData.append('files', file);
      formData.append('paths', relativePath);
    }

    const response = await fetch(`${API_BASE}/skills/admin/upload-folder`, {
      method: 'POST',
      credentials: 'include',
      body: formData,
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Upload failed' }));
      throw new Error(error.message || error.error || 'Upload failed');
    }

    return response.json();
  },

  /**
   * Delete a shared skill (admin only)
   */
  deleteSkill(name: string): Promise<{ success: boolean; message: string }> {
    return request(`/skills/admin/${name}`, {
      method: 'DELETE',
    });
  },
};

// ============================================
// Claude Types
// ============================================

export interface ClaudeAuthStatus {
  cliInstalled: boolean;
  authenticated: boolean;
  accountEmail?: string;
  error?: string;
  activeFlow?: {
    status: string;
    authUrl?: string;
  };
}

export interface ClaudeConnectResponse {
  success: boolean;
  authUrl?: string;
  status?: string;
  message?: string;
  alreadyAuthenticated?: boolean;
  accountEmail?: string;
}

export interface ClaudeCheckAuthResponse {
  authenticated: boolean;
  accountEmail?: string;
}

// ============================================
// Claude API
// ============================================

export interface ClaudeSubmitCodeResponse {
  success: boolean;
  authenticated?: boolean;
  accountEmail?: string;
  error?: string;
}

export const claudeApi = {
  /**
   * Get Claude CLI authentication status
   */
  getStatus(): Promise<ClaudeAuthStatus> {
    return request('/claude/status');
  },

  /**
   * Start Claude CLI authentication flow
   */
  connect(): Promise<ClaudeConnectResponse> {
    return request('/claude/connect', { method: 'POST' });
  },

  /**
   * Check if auth flow completed
   */
  checkAuth(): Promise<ClaudeCheckAuthResponse> {
    return request('/claude/check-auth', { method: 'POST' });
  },

  /**
   * Submit authentication code from OAuth flow
   */
  submitCode(code: string): Promise<ClaudeSubmitCodeResponse> {
    return request('/claude/submit-code', { method: 'POST', body: { code } });
  },

  /**
   * Disconnect from Claude
   */
  disconnect(): Promise<{ success: boolean; message?: string }> {
    return request('/claude/disconnect', { method: 'POST' });
  },

  /**
   * Cancel active auth flow
   */
  cancel(): Promise<{ success: boolean; message?: string }> {
    return request('/claude/cancel', { method: 'POST' });
  },
};

// ============================================
// Codex Types
// ============================================

export interface CodexAuthStatus {
  cliInstalled: boolean;
  authenticated: boolean;
  error?: string;
}

// ============================================
// Codex API
// ============================================

export const codexApi = {
  /**
   * Get Codex CLI authentication status
   */
  getStatus(): Promise<CodexAuthStatus> {
    return request('/codex/status');
  },

  /**
   * Prepare Codex CLI authentication environment
   */
  connect(): Promise<{ success: boolean; message?: string }> {
    return request('/codex/connect', { method: 'POST' });
  },

  /**
   * Disconnect from Codex
   */
  disconnect(): Promise<{ success: boolean; message?: string }> {
    return request('/codex/disconnect', { method: 'POST' });
  },
};

// ============================================
// Skills Types
// ============================================

export interface SkillInfo {
  name: string;
  title: string;
  description: string;
  version?: string;
  userInvocable?: boolean;
  shareable?: boolean;
  dependencies?: string[];
  produces?: { path: string; servedAt?: string }[];
  fullContent?: string;
}

export interface SkillsListResponse {
  skills: SkillInfo[];
}

export interface SkillDetailResponse extends SkillInfo {
  fullContent: string;
}

// ============================================
// Skills API
// ============================================

export const skillsApi = {
  /**
   * List all skills for the current user
   */
  list(): Promise<SkillsListResponse> {
    return request('/skills');
  },

  /**
   * Get full details for a specific skill
   */
  get(name: string): Promise<SkillDetailResponse> {
    return request(`/skills/${encodeURIComponent(name)}`);
  },

  /**
   * List shared skills available to all users
   */
  listShared(): Promise<SkillsListResponse> {
    return request('/skills/shared/list');
  },

  /**
   * Install a shared skill to user's directory
   */
  install(name: string): Promise<{ success: boolean; message: string }> {
    return request(`/skills/install/${encodeURIComponent(name)}`, { method: 'POST' });
  },

  /**
   * Uninstall a skill from user's directory
   */
  uninstall(name: string): Promise<{ success: boolean; message: string }> {
    return request(`/skills/${encodeURIComponent(name)}`, { method: 'DELETE' });
  },
};

export type PinKind = 'file' | 'dashboard' | 'chat';

export interface Pin {
  kind: PinKind;
  entityId: string;
  label: string;
  sublabel?: string;
  pinnedAt: string;
}

export const pinsApi = {
  list(): Promise<{ pins: Pin[] }> {
    return request('/pins');
  },

  add(kind: PinKind, entityId: string): Promise<{ pin: { kind: PinKind; entityId: string; pinnedAt: string } }> {
    return request('/pins', { method: 'POST', body: { kind, entityId } });
  },

  remove(kind: PinKind, entityId: string): Promise<{ removed: boolean }> {
    return request(`/pins/${encodeURIComponent(kind)}/${encodeURIComponent(entityId)}`, {
      method: 'DELETE',
    });
  },
};

// ============================================
// PCAP Types
// ============================================

export interface PcapStatus {
  available: boolean;
  version?: string;
  error?: string;
}

export interface PacketSummary {
  number: number;
  time: string;
  source: string;
  destination: string;
  protocol: string;
  length: number;
  info: string;
}

export interface PcapStats {
  totalPackets: number;
  displayedPackets: number;
  duration: string;
  limited: boolean;
}

export interface PcapParseResponse {
  packets: PacketSummary[];
  stats: PcapStats;
}

export interface ProtocolStat {
  name: string;
  frames: number;
  bytes: number;
}

export interface Conversation {
  addressA: string;
  addressB: string;
  packetsAtoB: number;
  bytesAtoB: number;
  packetsBtoA: number;
  bytesBtoA: number;
}

// ============================================
// PCAP API
// ============================================

export const pcapApi = {
  /**
   * Check if tshark is installed
   */
  getStatus(): Promise<PcapStatus> {
    return request('/pcap/status');
  },

  /**
   * Parse a PCAP file
   */
  parse(path: string, options?: { limit?: number; filter?: string }): Promise<PcapParseResponse> {
    return request('/pcap/parse', {
      method: 'POST',
      body: { path, ...options },
    });
  },

  /**
   * Get protocol statistics
   */
  getProtocols(path: string): Promise<{ protocols: ProtocolStat[] }> {
    return request('/pcap/protocols', {
      method: 'POST',
      body: { path },
    });
  },

  /**
   * Get conversation statistics
   */
  getConversations(path: string, type?: string): Promise<{ conversations: Conversation[] }> {
    return request('/pcap/conversations', {
      method: 'POST',
      body: { path, type },
    });
  },
};

// ============================================
// Browser Trace Types
// ============================================

export interface BrowserTraceUploadResponse {
  ok: boolean;
  traceId: string;
  path: string;
  absolutePath: string;
  entries: number;
}

export interface BrowserTraceFile {
  name: string;
  path: string;
  size: number;
  mtime: string;
}

export interface BrowserTracesResponse {
  traces: BrowserTraceFile[];
}

export interface BrowserTraceRouteCandidate {
  id: string;
  method: string;
  scheme: string;
  host: string;
  path: string;
  normalizedPath: string;
  requestType: 'form' | 'json' | 'query' | 'none' | 'unknown';
  responseType: 'json' | 'html' | 'text' | 'binary' | 'empty' | 'unknown';
  statusCodes: number[];
  count: number;
  queryKeys: string[];
  formKeys: string[];
  jsonKeys: string[];
  safety: 'read_observed' | 'auth_observed' | 'unknown_observed';
  confidence: 'high' | 'medium' | 'needs_review';
  suggestedOperation: string;
  notes: string[];
}

export interface BrowserTraceAnalysis {
  traceName: string;
  generatedAt: string;
  entryCount: number;
  hostCount: number;
  primaryHost: string | null;
  providerGuess: string | null;
  routes: BrowserTraceRouteCandidate[];
}

// ============================================
// Browser Trace API
// ============================================

export const browserTracesApi = {
  upload(har: unknown): Promise<BrowserTraceUploadResponse> {
    return request('/browser-traces', {
      method: 'POST',
      body: { har },
    });
  },

  list(): Promise<BrowserTracesResponse> {
    return request('/browser-traces');
  },

  rename(name: string, nextName: string): Promise<{ trace: BrowserTraceFile }> {
    return request(`/browser-traces/${encodeURIComponent(name)}`, {
      method: 'PATCH',
      body: { name: nextName },
    });
  },

  delete(name: string): Promise<{ success: boolean; name: string }> {
    return request(`/browser-traces/${encodeURIComponent(name)}`, { method: 'DELETE' });
  },

  deleteAll(): Promise<{ success: boolean; deleted: number }> {
    return request('/browser-traces', { method: 'DELETE' });
  },

  analyze(name: string): Promise<{ analysis: BrowserTraceAnalysis }> {
    return request(`/browser-traces/${encodeURIComponent(name)}/analyze`, { method: 'POST' });
  },
};

export const rateDecksApi = {
  getDomesticServiceSummary(input: {
    serviceDefinitionId: string;
    providerCalendarEffectiveAt: string;
  }): Promise<DomesticServiceRateSummary> {
    return request('/rate-decks/domestic/service-summary', {
      method: 'POST',
      body: input,
    });
  },

  getDomesticCustomerServiceSummary(input: {
    serviceDefinitionId: string;
    providerCalendarEffectiveAt: string;
  }): Promise<DomesticServiceRateSummary> {
    return request('/rate-decks/domestic/customer-service-summary', {
      method: 'POST',
      body: input,
    });
  },
};

export const trafficApi = {
  getDomesticPerformance(input: {
    serviceDefinitionId: string;
    providerCalendarStartDate: string;
    providerCalendarEndDate: string;
  }): Promise<DomesticTrafficPerformanceReport> {
    return request('/traffic/domestic/performance', {
      method: 'POST',
      body: input,
    });
  },

  createInvestigation(input: {
    chatSessionId: string;
    question: string;
    serviceDefinitionId: string;
    currentPeriod: { startDate: string; endDate: string };
    comparisonPeriod: { startDate: string; endDate: string };
  }): Promise<TrafficInvestigationRunResponse> {
    return request('/traffic/domestic/investigations', { method: 'POST', body: input });
  },

  preflightInvestigation(input: {
    chatSessionId: string;
    question: string;
    serviceDefinitionId: string;
    currentPeriod: TrafficInvestigationPeriod;
    comparisonPeriod: TrafficInvestigationPeriod;
  }): Promise<TrafficInvestigationPreflightResponse> {
    return request('/traffic/domestic/investigations/preflight', { method: 'POST', body: input });
  },

  startInvestigationRun(input: {
    draftId: string;
    chatSessionId: string;
    expectedDraftRevision: number;
  }): Promise<TrafficInvestigationRunResponse> {
    const { draftId, ...body } = input;
    return request(`/traffic/domestic/investigations/${encodeURIComponent(draftId)}/runs`, {
      method: 'POST',
      body,
    });
  },

  async getInvestigationRuns(draftId: string): Promise<TrafficEvidenceRun[]> {
    const response = await request<TrafficInvestigationRunsResponse>(
      `/traffic/domestic/investigations/drafts/${encodeURIComponent(draftId)}/runs`,
    );
    return response.runs;
  },

  getInvestigationDraft(draftId: string): Promise<TrafficInvestigationDraftResponse> {
    return request(
      `/traffic/domestic/investigations/drafts/${encodeURIComponent(draftId)}`,
    );
  },

  getInvestigation(caseId: string): Promise<TrafficInvestigationCase> {
    return request(`/traffic/domestic/investigations/${encodeURIComponent(caseId)}`);
  },

  getLatestInvestigation(): Promise<TrafficInvestigationCase | null> {
    return request('/traffic/domestic/investigations/latest');
  },

  confirmInvestigation(caseId: string): Promise<TrafficInvestigationCase> {
    return request(`/traffic/domestic/investigations/${encodeURIComponent(caseId)}/confirm`, {
      method: 'POST',
    });
  },

  addInvestigationFeedback(caseId: string, input: {
    helpful?: boolean;
    note?: string;
  }): Promise<TrafficInvestigationCase> {
    return request(`/traffic/domestic/investigations/${encodeURIComponent(caseId)}/feedback`, {
      method: 'POST',
      body: input,
    });
  },
};

// ============================================
// Combined API Export
// ============================================

export const api = {
  auth: authApi,
  files: filesApi,
  workspace: {
    assets: workspaceAssetsApi,
  },
  chat: chatApi,
  system: systemApi,
  connectors: switchConnectorsApi,
  carrierInitialization: carrierInitializationApi,
  rateDecks: rateDecksApi,
  traffic: trafficApi,
  admin: adminApi,
  claude: claudeApi,
  codex: codexApi,
  skills: skillsApi,
  pins: pinsApi,
  pcap: pcapApi,
  browserTraces: browserTracesApi,
  learning: learningApi,
};

export default api;
